import Button from 'components/_UI/Button';
import Text from 'components/_UI/Text';
import React, { useState } from 'react'
import { Calendar, Globe, Key, PlusSquare, ShieldCheck, Trash2, User } from 'lucide-react';
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column'
import { Row } from 'styled/alignment/Row'
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import teamsApi from 'api/requests/teams';
import InfoTooltip from 'components/_UI/InfoTooltip';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import TeamMemberModal from './modals/TeamMemberModal';
import TeamGrantModal from './modals/TeamGrantModal';
import ConfirmDialog from 'modules/restaurant/menu/components/ConfirmDialog';
import { getTeamIcon } from './teamIcons';
import useTeamPermissionCatalog from './useTeamPermissionCatalog';
import usePanelPermissions from 'hooks/usePanelPermissions';
import { PERMISSIONS } from 'helpers/panelPermissions';

const MEMBER_STATUS_MAP = {
    accepted: { label: 'Aceito', bg: '#DCFCE7', color: '#166534' },
    pending: { label: 'Pendente', bg: '#FEF3C7', color: '#92400E' },
    rejected: { label: 'Recusado', bg: '#FEE2E2', color: '#DC2626' },
};

function initials(email) {
    return (email || '').trim().slice(0, 2) || '?';
}

export default function TeamDetailPanel({ team, onChange }) {
    const TeamIconComponent = getTeamIcon(team.icon);
    const [memberModalVisible, setMemberModalVisible] = useState(false);
    const [grantModalVisible, setGrantModalVisible] = useState(false);
    const [confirmRemoveMember, setConfirmRemoveMember] = useState({ open: false, id: null, email: '' });
    const [confirmRemoveGrant, setConfirmRemoveGrant] = useState({ open: false, id: null, label: '' });
    const [busy, setBusy] = useState(false);
    const { toast, toasts } = useToast();
    const { labelOf } = useTeamPermissionCatalog();
    const { can } = usePanelPermissions();
    const canManage = can(PERMISSIONS.TEAMS_MANAGE);

    function onMemberSuccess() {
        setMemberModalVisible(false);
        onChange();
        toast.success('Convite enviado com sucesso!', { position: 'top-center' })
    }

    function onRemoveMemberClick(member) {
        setConfirmRemoveMember({ open: true, id: member.id, email: member.email });
    }

    async function handleRemoveMember() {
        setBusy(true);

        try {
            await teamsApi.removeMember(team.id, confirmRemoveMember.id);
            onChange();
            toast.success('Membro removido com sucesso!', { position: 'top-center' })
        } catch (error) {

        } finally {
            setBusy(false);
            setConfirmRemoveMember({ open: false, id: null, email: '' });
        }
    }

    function onGrantSuccess() {
        setGrantModalVisible(false);
        onChange();
        toast.success('Acesso concedido com sucesso!', { position: 'top-center' })
    }

    function onRemoveGrantClick(grant) {
        setConfirmRemoveGrant({
            open: true,
            id: grant.id,
            label: grant.scope_type === 'panel' ? 'o painel inteiro' : `o evento "${grant.event?.name}"`,
        });
    }

    async function handleRemoveGrant() {
        setBusy(true);

        try {
            await teamsApi.removeGrant(team.id, confirmRemoveGrant.id);
            onChange();
            toast.success('Acesso revogado com sucesso!', { position: 'top-center' })
        } catch (error) {

        } finally {
            setBusy(false);
            setConfirmRemoveGrant({ open: false, id: null, label: '' });
        }
    }

    const members = team.members || [];
    const grants = team.grants || [];

    return (
        <Container>
            <ToastContainer toasts={toasts} />

            <Header>
                <HeaderLeft>
                    <TitleRow>
                        <TitleIcon>
                            <TeamIconComponent size={16} color={panelAccent(colors.secondaryPurple)} />
                        </TitleIcon>
                        <Text name='subtitle' color={colors.nightDark} paddingBottom={0}>
                            {team.name}
                        </Text>
                    </TitleRow>

                    <Text marginTop={'4px'}>
                        Gerencie os membros e onde essa equipe tem acesso
                    </Text>
                </HeaderLeft>

                {canManage && (
                    <Button
                        variant='featured'
                        category='squired'
                        onClick={() => setMemberModalVisible(true)}
                    >
                        <PlusSquare color={colors.neutralLight} size={16} />
                        <Text
                            color={colors.neutralLight}
                            paddingBottom={0}
                            marginLeft={'6px'}
                        >
                            Adicionar membro
                        </Text>
                    </Button>
                )}
            </Header>

            {
                memberModalVisible && (
                    <TeamMemberModal
                        visible={memberModalVisible}
                        teamId={team.id}
                        onClose={() => setMemberModalVisible(false)}
                        onSuccess={onMemberSuccess}
                    />
                )
            }

            {
                grantModalVisible && (
                    <TeamGrantModal
                        visible={grantModalVisible}
                        teamId={team.id}
                        existingGrants={team.grants || []}
                        onClose={() => setGrantModalVisible(false)}
                        onSuccess={onGrantSuccess}
                    />
                )
            }

            <ConfirmDialog
                isOpen={confirmRemoveMember.open}
                title='Remover membro'
                message={`Tem certeza que deseja remover "${confirmRemoveMember.email}" desta equipe?`}
                confirmLabel='Remover'
                danger
                loading={busy}
                onConfirm={handleRemoveMember}
                onCancel={() => setConfirmRemoveMember({ open: false, id: null, email: '' })}
            />

            <ConfirmDialog
                isOpen={confirmRemoveGrant.open}
                title='Revogar acesso'
                message={`Tem certeza que deseja revogar o acesso desta equipe a ${confirmRemoveGrant.label}? Os membros perdem esse acesso imediatamente.`}
                confirmLabel='Revogar'
                danger
                loading={busy}
                onConfirm={handleRemoveGrant}
                onCancel={() => setConfirmRemoveGrant({ open: false, id: null, label: '' })}
            />

            <SectionBlock>
                <SideTitle>
                    <Key size={14} color={colors.night} />
                    Permissões da equipe
                </SideTitle>

                {
                    (team.permissions || []).length ? (
                        <PermissionsWrap>
                            {team.permissions.map(permission => (
                                <PermissionPill key={permission}>
                                    {labelOf(permission)}
                                </PermissionPill>
                            ))}
                        </PermissionsWrap>
                    ) : (
                        <Text marginTop={'10px'}>
                            Nenhuma permissão definida — os membros não conseguem fazer nada até você definir. Use o botão de editar no topo do painel.
                        </Text>
                    )
                }
            </SectionBlock>

            <SectionBlock>
                <SideTitle>
                    <User size={14} color={colors.night} />
                    Membros
                    {members.length > 0 && <Count>{members.length}</Count>}
                </SideTitle>

                {
                    members.length ? (
                        <MembersGrid>
                            {members.map(member => {
                                const statusInfo = MEMBER_STATUS_MAP[member.status] || MEMBER_STATUS_MAP.pending;

                                return (
                                    <MemberCard key={member.id}>
                                        <Avatar>{initials(member.email)}</Avatar>
                                        <MemberInfo>
                                            <MemberEmail title={member.email}>{member.email}</MemberEmail>
                                            <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>{statusInfo.label}</StatusBadge>
                                        </MemberInfo>
                                        {canManage && (
                                            <InfoTooltip text='Remover' type='none'>
                                                <RemoveButton onClick={() => onRemoveMemberClick(member)}>
                                                    <Trash2 color={colors.danger} size={16} />
                                                </RemoveButton>
                                            </InfoTooltip>
                                        )}
                                    </MemberCard>
                                );
                            })}
                        </MembersGrid>
                    ) : (
                        <Text marginTop={'10px'}>
                            Ainda não tem ninguém nesta equipe.
                        </Text>
                    )
                }
            </SectionBlock>

            <SectionBlock>
                <SectionLabelRow>
                    <SideTitle>
                        <ShieldCheck size={14} color={colors.night} />
                        Onde esta equipe tem acesso
                        {grants.length > 0 && <Count>{grants.length}</Count>}
                    </SideTitle>

                    {canManage && (
                        <Button
                            variant='light'
                            category='roundedBorded'
                            onClick={() => setGrantModalVisible(true)}
                        >
                            <PlusSquare color={panelAccent(colors.secondaryPurple)} size={16} />
                            <Text color={panelAccent(colors.secondaryPurple)} paddingBottom={0} marginLeft={'6px'}>
                                Conceder acesso
                            </Text>
                        </Button>
                    )}
                </SectionLabelRow>

                {
                    grants.length ? (
                        <AccessList>
                            {grants.map(grant => {
                                const isPanel = grant.scope_type === 'panel';

                                return (
                                    <AccessCard key={grant.id}>
                                        <AccessIcon>
                                            {isPanel ? <Globe size={16} color={panelAccent(colors.secondaryPurple)} /> : <Calendar size={16} color={panelAccent(colors.secondaryPurple)} />}
                                        </AccessIcon>
                                        <AccessInfo>
                                            <AccessLabel>{isPanel ? 'Painel inteiro' : (grant.event?.name || 'Evento')}</AccessLabel>
                                            <AccessSub>{isPanel ? 'Painel do produtor, com as permissões da equipe' : 'Evento específico (validação e gestão do evento)'}</AccessSub>
                                        </AccessInfo>
                                        {canManage && (
                                            <InfoTooltip text='Revogar' type='none'>
                                                <RemoveButton onClick={() => onRemoveGrantClick(grant)}>
                                                    <Trash2 color={colors.danger} size={16} />
                                                </RemoveButton>
                                            </InfoTooltip>
                                        )}
                                    </AccessCard>
                                );
                            })}
                        </AccessList>
                    ) : (
                        <Text marginTop={'10px'}>
                            Esta equipe ainda não tem acesso a nenhum evento ou ao painel.
                        </Text>
                    )
                }
            </SectionBlock>
        </Container>
    )
}

const Container = styled(Column)`
    width: 100%;
`;

const Header = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 18px;
    margin-bottom: 20px;
    border-bottom: 1px solid ${colors.neutralDark};

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;

        & > button {
            width: auto;
        }
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TitleIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    flex-shrink: 0;
`;

const SectionBlock = styled.div`
    margin-top: 26px;

    &:first-of-type {
        margin-top: 0;
    }
`;

const SectionLabelRow = styled(Row)`
    width: 100%;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 8px;

    & > h4 {
        margin-bottom: 0;
    }

    & > button {
        width: auto;
    }
`;

const SideTitle = styled.h4`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 8px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Count = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: 6px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};
    text-transform: none;
    letter-spacing: 0;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    background: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
`;

const PermissionsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const PermissionPill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};
`;

const MembersGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const MemberCard = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}1A;
    box-shadow: 0 1px 2px ${colors.nightDark}0F;
    flex: 1 1 240px;
    max-width: 320px;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;

    &:hover {
        border-color: ${panelAccentAlpha(colors.secondaryPurple, '40')};
        box-shadow: 0 4px 12px ${colors.nightDark}14;
    }
`;

const Avatar = styled.div`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '18')};
    color: ${panelAccent(colors.secondaryPurple)};
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
    text-transform: uppercase;
`;

const MemberInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    min-width: 0;
    flex: 1;
`;

const MemberEmail = styled.span`
    width: 100%;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s ease;
    padding: 0;

    &:hover {
        background: ${colors.neutralBackground};
    }
`;

const AccessList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const AccessCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}1A;
    box-shadow: 0 1px 2px ${colors.nightDark}0F;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;

    &:hover {
        border-color: ${panelAccentAlpha(colors.secondaryPurple, '40')};
        box-shadow: 0 4px 12px ${colors.nightDark}14;
    }
`;

const AccessIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    flex-shrink: 0;
`;

const AccessInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

const AccessLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AccessSub = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
`;
